import React from "react";
import { styled } from "@mui/material/styles";
import Card from "@mui/material/Card";
import Box from "@mui/material/Box";
import LanguageRoundedIcon from "@mui/icons-material/LanguageRounded";
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import LaunchRoundedIcon from "@mui/icons-material/LaunchRounded";
import VideoPreviewModal from "./VideoPreviewModal.web";
import { Tooltip } from "@mui/material";

const StyledCard = styled(Card)(({ theme }) => ({
  cursor: "pointer",
  overflow: "hidden",
  position: "relative",
  transition: "all 0.3s",
  aspectRatio: "4/3",
  width: "100%",
  borderRadius: "10px",
  backgroundColor: "#D7E8FF",
  boxShadow: "none",
}));

const CardImage = styled("img")({
  width: "100%",
  height: "100%",
  objectFit: "contain",
  backgroundColor: "#000",
});

const Placeholder = styled(Box)(({ theme }) => ({
  backgroundColor: "#D7E8FF",
  height: "100%",
  width: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
}));

interface Props {
  url: string;
}

interface S {
  isPreviewOpen: boolean;
  imageError: boolean;
}

class PreviousWorkCard extends React.Component<Props, S> {
  state: S = {
    isPreviewOpen: false,
    imageError: false,
  };

  getDomain = (url: string) => {
    try {
      return new URL(url).hostname.replace("www.", "");
    } catch {
      return "Invalid URL";
    }
  };

  isYouTube = (url: string) =>
    url.includes("youtube.com") || url.includes("youtu.be");

  isVimeo = (url: string) => url.includes("vimeo.com");

  isVideoLink = (url: string) => this.isYouTube(url) || this.isVimeo(url);

  isImageUrl = (url: string) => /\.(jpeg|jpg|png|webp|gif|svg)$/i.test(url);

  getYouTubeId = (url: string) => {
    const regExp =
      /^.*(?:youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return match && match[1].length === 11 ? match[1] : null;
  };

  getVimeoId = (url: string) => {
    const match = url.match(/vimeo\.com\/(\d+)/);
    return match ? match[1] : null;
  };

  getVideoThumbnail = (url: string): string | null => {
    if (this.isYouTube(url)) {
      const id = this.getYouTubeId(url);
      return id ? `https://img.youtube.com/vi/${id}/hqdefault.jpg` : null;
    }
    if (this.isVimeo(url)) {
      const id = this.getVimeoId(url);
      return id ? `https://vumbnail.com/${id}.jpg` : null;
    }
    return null;
  };

  getImageUrl = () => {
    const { url } = this.props;
    if (this.isVideoLink(url)) {
      return this.getVideoThumbnail(url);
    }
    if (this.isImageUrl(url)) {
      return url;
    }
    return null;
  };

  handleCardClick = () => {
    if (this.isVideoLink(this.props.url)) {
      this.setState({ isPreviewOpen: true });
    } else {
      window.open(this.props.url, "_blank");
    }
  };

  handleClosePreview = () => {
    this.setState({ isPreviewOpen: false });
  };

  render() {
    const { url } = this.props;
    const { isPreviewOpen, imageError } = this.state;

    const imageUrl = this.getImageUrl();
    const isVideo = this.isVideoLink(url);
    const iconTitle = isVideo ? "Play Video" : this.getDomain(url);

    return (
      <>
        <StyledCard onClick={this.handleCardClick}>
          {imageUrl && !imageError ? (
            <CardImage
              src={imageUrl}
              alt={this.getDomain(url)}
              onError={() => this.setState({ imageError: true })}
            />
          ) : (
            <Placeholder>
              <LanguageRoundedIcon fontSize="large" />
            </Placeholder>
          )}

          <Box
            position="absolute"
            top={0}
            left={0}
            right={0}
            bottom={0}
            sx={{
              bgcolor: "rgba(0,0,0,0.5)",
              opacity: 0,
              "&:hover": { opacity: 1 },
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Tooltip title={iconTitle}>
              <Box
                sx={{
                  p: 1.5,
                  backgroundColor: "rgba(255,255,255,0.2)",
                  borderRadius: "50%",
                  backdropFilter: "blur(4px)",
                }}
              >
                {isVideo ? (
                  <PlayArrowRoundedIcon sx={{ color: "#fff" }} />
                ) : (
                  <LaunchRoundedIcon sx={{ color: "#fff" }} />
                )}
              </Box>
            </Tooltip>
          </Box>
        </StyledCard>

        <VideoPreviewModal
          url={url}
          isOpen={isPreviewOpen}
          onClose={this.handleClosePreview}
        />
      </>
    );
  }
}

export default PreviousWorkCard;
